import { addDays, addMonths } from 'date-fns';

import { DEFAULT_PAGETYPE, EXPORT_TIMESERIES_PANEL, EXPORT_EVENT_PANEL } from '../../constants';
import { getQueryParameters } from '../utils';
import {
  SET_ACTIVE_PAGE_TYPE,
  SET_ACTIVE_COMPONENT_FILTER,
  SET_ACTIVE_MODEL,
  SET_ZOOM_COMPONENT,
  SET_VIEWER_READY,
  SET_TIMEZONE,
  RECEIVE_ACTIVE_COMPONENT,
  RECEIVE_POLLING_DATE_RANGE,
  RECEIVE_POLLING_INTERVAL,
  RECEIVE_REFRESH,
  CLEAR_SITE_DATA,
  SET_POLLING_ACTIVE,
  SET_POLLING_ACTIVE_DONE,
  CREATE_PANEL_DATE_SUBSCRIPTION,
  DESTROY_PANEL_DATE_SUBSCRIPTION,
} from './actions';
import { getGranularityForDateRange, pollingIntervals } from './helpers';

export const getInitialPollingDateRange = (panelId) => {
  let defaultStart = addMonths(Date.now(), -2).getTime();
  let defaultEnd = addMonths(Date.now(), 1).getTime();

  if (panelId === EXPORT_TIMESERIES_PANEL) {
    defaultStart = addDays(Date.now(), -1).getTime();
    defaultEnd = Date.now();
  }

  return {
    startDate: defaultStart,
    endDate: defaultEnd,
    label: null,
    duration: defaultEnd - defaultStart,
    granularity: getGranularityForDateRange({ startDate: defaultStart, endDate: defaultEnd }),
  };
};

const initialPollingDateRange = getInitialPollingDateRange();
const initialPollingDateRangeTimeseriesExportPanel =
  getInitialPollingDateRange(EXPORT_TIMESERIES_PANEL);
const { endDate, startDate, activeComponent } = getQueryParameters();

if (startDate && endDate) {
  const start = Number.parseInt(startDate, 10);
  const end = Number.parseInt(endDate, 10);
  const cleanStart = Number.isNaN(start) ? Date.now() - 36_000 : start;
  const cleanEnd = Number.isNaN(end) ? Date.now() : end;
  initialPollingDateRange.startDate = cleanStart;
  initialPollingDateRange.endDate = cleanEnd;
  initialPollingDateRange.duration = cleanEnd - cleanStart;
  initialPollingDateRange.granularity = getGranularityForDateRange({
    startDate: cleanStart,
    endDate: cleanEnd,
  });
}

const initialState = {
  activeComponentId: activeComponent || null,
  activeComponentFilter: null,
  pageType: DEFAULT_PAGETYPE,
  zoomComponentId: null,
  activeModelId: null,
  viewerReady: false,
  pollingDateRanges: {
    global: initialPollingDateRange,
    timeseriesExportPanel: initialPollingDateRangeTimeseriesExportPanel,
  },
  pollingInterval: pollingIntervals.find((p) => p.default).value,
  isPollingActive: {
    components: false,
    files: false,
    sources: false,
    variables: false,
    visualizations: false,
  },
  refreshedAt: null,
  timezone: 'Europe/Stockholm',
};

export function reducer(state = structuredClone(initialState), action) {
  switch (action.type) {
    case RECEIVE_ACTIVE_COMPONENT: {
      return { ...state, activeComponentId: action.activeComponentId };
    }
    case SET_ACTIVE_COMPONENT_FILTER: {
      return { ...state, activeComponentFilter: action.activeComponentFilter };
    }
    case SET_ACTIVE_PAGE_TYPE: {
      return { ...state, pageType: action.pageType };
    }
    case SET_ZOOM_COMPONENT: {
      return { ...state, zoomComponentId: action.id };
    }
    case SET_ACTIVE_MODEL: {
      return { ...state, activeModelId: action.modelId };
    }
    case SET_VIEWER_READY: {
      return { ...state, viewerReady: action.viewerReady };
    }
    case RECEIVE_POLLING_DATE_RANGE: {
      if (action.panelId) {
        return {
          ...state,
          pollingDateRanges: {
            ...state.pollingDateRanges,
            [action.panelId]: action.pollingDateRange,
          },
        };
      }
      return {
        ...state,
        pollingDateRanges: Object.fromEntries(
          Object.keys(state.pollingDateRanges).map((panelId) => [panelId, action.pollingDateRange])
        ),
      };
    }
    case RECEIVE_POLLING_INTERVAL: {
      return { ...state, pollingInterval: action.pollingInterval };
    }
    case SET_TIMEZONE: {
      return { ...state, timezone: action.timezone };
    }
    case RECEIVE_REFRESH: {
      return { ...state, refreshedAt: action.refreshedAt };
    }
    case SET_POLLING_ACTIVE: {
      return {
        ...state,
        isPollingActive: {
          ...state.isPollingActive,
          [action.component]: true,
        },
      };
    }
    case SET_POLLING_ACTIVE_DONE: {
      return {
        ...state,
        isPollingActive: {
          ...state.isPollingActive,
          [action.component]: false,
        },
      };
    }
    case CREATE_PANEL_DATE_SUBSCRIPTION: {
      const { panelId, initializeFromGlobal = true } = action;

      // If panel already has a stored range, preserve it
      if (state.pollingDateRanges[panelId]) {
        return state;
      }

      let range;
      if (panelId === EXPORT_TIMESERIES_PANEL && state.pollingDateRanges.timeseriesExportPanel) {
        range = state.pollingDateRanges.timeseriesExportPanel;
      } else if (initializeFromGlobal) {
        range = state.pollingDateRanges.global;
      } else {
        // Don't inherit from global - use default range
        range = getInitialPollingDateRange(panelId);
      }

      return {
        ...state,
        pollingDateRanges: {
          ...state.pollingDateRanges,
          [panelId]: range,
        },
      };
    }
    case DESTROY_PANEL_DATE_SUBSCRIPTION: {
      const { panelId } = action;
      // Keep panel date ranges for export panels
      // For all other panels, preserve the range so it persists across navigation
      if (panelId === EXPORT_TIMESERIES_PANEL || panelId === EXPORT_EVENT_PANEL) {
        const pollingDateRanges = {
          ...state.pollingDateRanges,
          [panelId]: getInitialPollingDateRange(panelId),
        };
        return {
          ...state,
          pollingDateRanges,
        };
      }
      return state;
    }
    case CLEAR_SITE_DATA: {
      return {
        ...structuredClone(initialState),
        // Kept across the reset.
        pageType: state.pageType,
        timezone: state.timezone,
        // initialState holds values from the URL query and load time, which must not come back.
        activeComponentId: null,
        pollingDateRanges: {
          global: getInitialPollingDateRange(),
          timeseriesExportPanel: getInitialPollingDateRange(EXPORT_TIMESERIES_PANEL),
        },
      };
    }
    default: {
      return state;
    }
  }
}
